import { TbCircleCheckFilled, TbCircleHalf2, TbCircleXFilled, TbMinus } from "solid-icons/tb";
import { EXPLORE_PLAN } from "~/components/billing/planFeatures";

/**
 * The sourced Chief vs Granola / Otter / Fathom data. The /compare page renders
 * all of it; the homepage picks rows by id, so the two can never disagree.
 */

export const SRC = {
  granolaPricing: "https://www.granola.ai/pricing",
  granolaIntegrations: "https://www.granola.ai/blog/granola-integrations-complete-guide-connecting-meeting-tools",
  granolaMcp: "https://docs.granola.ai/help-center/sharing/integrations/mcp",
  granolaPhoneCalls: "https://www.granola.ai/updates/introducing-notes-on-phone-calls",
  granolaTranscription: "https://docs.granola.ai/help-center/taking-notes/transcription",
  otterPricing: "https://otter.ai/pricing",
  otterApps: "https://otter.ai/apps",
  otterBusiness: "https://otter.ai/business",
  otterMcp: "https://otter.ai/blog/otter-mcp-your-meetings-now-power-every-tool-you-use",
  fathomPricing: "https://www.fathom.ai/pricing",
  fathomBotFree: "https://www.fathom.ai/learn/bot-free-note-taker",
  fathomOverview: "https://www.fathom.ai/overview",
  fathomIos: "https://apps.apple.com/app/id6751613402",
  fathomIntegrations: "https://www.fathom.ai/integrations",
  fathomMcp: "https://developers.fathom.ai/mcp-docs",
  fathomTranscript: "https://help.fathom.video/en/articles/296000",
  fathomSuperhuman: "https://www.fathom.ai/superhuman",
  granolaEnhancedNotes: "https://docs.granola.ai/help-center/taking-notes/ai-enhanced-notes",
  otterLiveSummary: "https://otter.ai/zoom",
  fathomLiveSummary: "https://help.fathom.video/en/articles/7573633",
};

export const SRC_NAME: Record<string, string> = {
  [SRC.granolaPricing]: "pricing",
  [SRC.granolaMcp]: "MCP docs",
  [SRC.granolaPhoneCalls]: "phone calls",
  [SRC.granolaTranscription]: "transcription",
  [SRC.otterPricing]: "pricing",
  [SRC.otterApps]: "apps",
  [SRC.otterBusiness]: "Business plan",
  [SRC.otterMcp]: "MCP",
  [SRC.fathomPricing]: "pricing",
  [SRC.fathomBotFree]: "bot-free recording",
  [SRC.fathomOverview]: "overview",
  [SRC.fathomIos]: "iPhone app",
  [SRC.fathomIntegrations]: "integrations",
  [SRC.fathomMcp]: "MCP docs",
};

export type Status = "yes" | "partly" | "no" | "unknown";
export type Cell = { status: Status; note?: string; href?: string };
type Row = { id: string; label: string; chief: Cell; granola: Cell; otter: Cell; fathom: Cell };

// "unknown" means we found no mention on the vendor's own pages, which is not the same as "no".
export const STATUS: Record<Status, { icon: typeof TbMinus; label: string; class: string }> = {
  yes: { icon: TbCircleCheckFilled, label: "Yes", class: "text-[#1d9e75]" },
  partly: { icon: TbCircleHalf2, label: "Partly", class: "text-accent" },
  no: { icon: TbCircleXFilled, label: "No", class: "text-secondary-text" },
  unknown: { icon: TbMinus, label: "Not documented", class: "text-secondary-text/60" },
};

export const TABLE_GROUPS: { name: string; rows: Row[] }[] = [
  {
    name: "Capture",
    rows: [
      {
        id: "bot_free",
        label: "Records without a bot joining the call",
        chief: { status: "yes" },
        granola: { status: "yes", href: SRC.granolaPricing },
        otter: { status: "partly", note: "With the desktop app", href: SRC.otterApps },
        fathom: { status: "partly", note: "Bot-free beta, Mac only", href: SRC.fathomBotFree },
      },
      {
        id: "iphone",
        label: "Records on iPhone",
        chief: { status: "yes", note: "In-person conversations; not Zoom, Meet, or phone calls" },
        granola: { status: "yes", href: SRC.granolaPricing },
        otter: { status: "yes", href: SRC.otterApps },
        fathom: { status: "partly", note: "App Store app, though Fathom's site says waitlist", href: SRC.fathomIos },
      },
    ],
  },
  {
    name: "During the call",
    rows: [
      {
        id: "coedit",
        label: "Co-edit documents while you talk",
        chief: { status: "yes" },
        granola: { status: "unknown" },
        otter: { status: "unknown" },
        fathom: { status: "unknown" },
      },
      {
        id: "live_transcript",
        label: "Shared live transcript",
        chief: { status: "yes", note: "Guests follow in a browser" },
        granola: { status: "unknown" },
        otter: { status: "yes", href: SRC.otterBusiness },
        fathom: { status: "unknown" },
      },
      {
        id: "live_notes",
        label: "AI notes that build during the call",
        chief: { status: "yes" },
        granola: { status: "no", note: "Generated after the call ends", href: SRC.granolaEnhancedNotes },
        otter: { status: "yes", href: SRC.otterLiveSummary },
        fathom: { status: "partly", note: "Mac desktop app only", href: SRC.fathomLiveSummary },
      },
    ],
  },
  {
    name: "After the call",
    rows: [
      {
        id: "updates_tools",
        label: "Updates Linear and GitHub for you",
        chief: { status: "yes", note: "On Pro, with your approval" },
        granola: { status: "partly", note: "Through Zapier", href: SRC.granolaIntegrations },
        otter: { status: "unknown" },
        fathom: { status: "partly", note: "Through Zapier or Make", href: SRC.fathomIntegrations },
      },
      {
        id: "drafts",
        label: "Turns the call into a PRD, brief, or plan",
        chief: { status: "yes" },
        granola: { status: "partly", note: "Note templates", href: SRC.granolaPricing },
        otter: { status: "partly", note: "Meeting templates, Pro and up", href: SRC.otterPricing },
        fathom: { status: "partly", note: "Meeting templates, Premium and up", href: SRC.fathomPricing },
      },
      {
        id: "agent_handoff",
        label: "Hands the meeting to Claude Code, Codex or Cursor",
        chief: { status: "yes", note: "Sends the session into the agent" },
        granola: { status: "partly", note: "Agents can read notes through MCP", href: SRC.granolaMcp },
        otter: { status: "partly", note: "Agents can read transcripts through MCP", href: SRC.otterMcp },
        fathom: { status: "partly", note: "Agents can read meetings through MCP", href: SRC.fathomMcp },
      },
      {
        id: "crm",
        label: "Syncs to your CRM",
        chief: { status: "partly", note: "HubSpot, Salesforce, Attio and Close through MCP, on Chief Pro" },
        granola: { status: "yes", note: "Business plan and up", href: SRC.granolaPricing },
        otter: { status: "yes", note: "From Pro, 1 user on Pro", href: SRC.otterPricing },
        fathom: { status: "yes", note: "Basic sync from Free, field sync on Business and up", href: SRC.fathomPricing },
      },
    ],
  },
  {
    name: "Your setup",
    rows: [
      {
        id: "byok",
        label: "Use your own model keys",
        chief: { status: "yes" },
        granola: { status: "unknown" },
        otter: { status: "unknown" },
        fathom: { status: "unknown" },
      },
      {
        id: "write_mcp",
        label: "MCP server that can write, not just read",
        chief: { status: "yes" },
        granola: { status: "no", note: "Read-only", href: SRC.granolaMcp },
        otter: { status: "unknown" },
        fathom: { status: "no", note: "Read-only", href: SRC.fathomMcp },
      },
    ],
  },
];

export const COMPETITORS = [
  { name: "Granola", src: "/images/compare/granola.png", bg: "" },
  { name: "Otter", src: "/images/compare/otter.png", bg: "bg-[#0a84ff]" },
  { name: "Fathom", src: "/images/compare/fathom.png", bg: "bg-[#00beff]" },
];

type Sourced = { lead: string; text: string; href: string };

export type Vendor = {
  key: "granola" | "otter" | "fathom";
  name: string;
  checked: string;
  title: string;
  description: string;
  h1: string;
  /** Answers the page's "[name] alternative" query, including where the vendor fits better. */
  answer: string;
  whyLook: Sourced[];
  quote?: { text: string; who: string; where: string; date: string; href: string };
  compare: { area: string; title: string; text: string; hrefs: string[] }[];
  pricing: { tiers: { name: string; price: string; note?: string }[]; team: string; href: string };
  switchIf: string[];
  stayIf: Sourced[];
  movingOver: { text: string; href: string };
  faq: { q: string; a: string; href?: string }[];
};

const HUB_LINE =
  "Chief reaches HubSpot, Salesforce, Attio and Close through MCP on its Pro plan, with your approval before anything is written.";
const BOT_FREE_FAQ = "Yes. Chief records from the desktop app on macOS or Windows, so no bot joins the call.";
const FREE_PLAN = `Chief's Explore plan is free: you get ${EXPLORE_PLAN.signupCredits.toLocaleString("en-US")} credits when you sign up, and free credits refill at ${EXPLORE_PLAN.dailyCredits} a day.`;

// `checked` is when each vendor's facts were last read on that vendor's own pages.
export const VENDORS: Vendor[] = [
  {
    key: "granola",
    name: "Granola",
    checked: "Oct 3, 2026",
    title: "Granola Alternative for Teams Who Work Live | Chief",
    description:
      "Looking for a Granola alternative? Chief records without a bot, and your team writes the plan together during the call. See pricing and where Granola fits better.",
    h1: "A Granola alternative\nwhere your team works on the call together",
    answer:
      "If you like recording without a bot but want your team working in one place during the call, Chief is a Granola alternative built for that. Chief records from your desktop, and people with editing access write the plan together while you talk. Granola still fits better if you record phone calls or on Android, or need Affinity.",
    whyLook: [
      {
        lead: "The free plan keeps 30 days.",
        text: "Granola Basic is free, but its meeting history covers 30 days. Unlimited history starts on Business at $14 per user a month.",
        href: SRC.granolaPricing,
      },
      {
        lead: "Engineering tools go through Zapier.",
        text: "Granola connects to Slack on every plan, adds Notion, HubSpot, Affinity, Attio and Zapier on Business, and Salesforce on Enterprise. Linear and GitHub connect through Zapier.",
        href: SRC.granolaIntegrations,
      },
    ],
    compare: [
      {
        area: "Capture",
        title: "No bot with either.\nChief records on Mac, Windows and iPhone.",
        text: "Both record without a bot, and both have an iPhone app for in-person conversations. Granola also records on Android and Apple Watch, and takes notes on phone calls.",
        hrefs: [SRC.granolaPricing, SRC.granolaPhoneCalls],
      },
      {
        area: "During the call",
        title: "Granola takes notes for you.\nChief puts your team to work on the call.",
        text: "Granola transcribes in real time and writes AI notes. In Chief, decisions and to-dos are tagged as the call goes, and people with editing access write shared Documents and Canvas together. Guests follow in a browser.",
        hrefs: [SRC.granolaTranscription],
      },
      {
        area: "After the call",
        title: "Granola sends the notes.\nChief updates Linear and GitHub.",
        text: "Granola sends notes to Slack, Notion, HubSpot, Affinity and Attio. On Pro, Chief updates Google, Notion, Slack, Linear and GitHub, and writes wait for your yes unless you pre-approved that kind of write.",
        hrefs: [SRC.granolaPricing],
      },
      {
        area: "Your setup",
        title: "Granola's MCP server reads.\nChief's reads and writes.",
        text: "Granola Business lets you choose the model, and its MCP server reads your notes. Chief's MCP server reads and writes, you can bring your own model keys on any plan, and your coding agent can start from the session.",
        hrefs: [SRC.granolaPricing, SRC.granolaMcp],
      },
    ],
    pricing: {
      tiers: [
        { name: "Basic", price: "$0", note: "30 days of meeting history" },
        { name: "Business", price: "$14 per user a month", note: "Unlimited history" },
        { name: "Enterprise", price: "$35 per user a month" },
      ],
      team: "A team of five on Granola Business pays $70 a month.",
      href: SRC.granolaPricing,
    },
    switchIf: [
      "You want the room working in one place during the call.",
      "You want decisions and to-dos sorted before the call ends.",
      "Your work runs through Linear or GitHub, and you'd rather approve an update than set up a Zap.",
      "You want an MCP server your tools can write to, not just read.",
    ],
    stayIf: [
      {
        lead: "You record phone calls, or on Android or your watch.",
        text: "Chief's iPhone app records in-person conversations, not phone calls.",
        href: SRC.granolaPricing,
      },
      {
        lead: "You need Affinity.",
        text: "Granola includes Affinity on Business. Chief reaches HubSpot, Salesforce, Attio and Close through MCP, but not Affinity.",
        href: SRC.granolaPricing,
      },
      {
        lead: "Thirty days of history is enough.",
        text: "Granola Basic costs nothing.",
        href: SRC.granolaPricing,
      },
    ],
    movingOver: {
      text: "Open a meeting in Granola and copy the whole transcript from its transcript panel. Save it as a text file, and Chief imports it as a session with a summary, decisions and to-dos. Do this for the meetings you still refer to, then run your next call in Chief next to Granola.",
      href: SRC.granolaTranscription,
    },
    faq: [
      { q: "Does Chief record without a bot, like Granola?", a: BOT_FREE_FAQ },
      {
        q: "Can my team work on the notes during the call?",
        a: "Yes. People with editing access write shared Documents and Canvas while you talk. Anyone with the guest link can follow the transcript, summary, and brief in a browser, without an account.",
      },
      {
        q: "Can I bring my Granola notes into Chief?",
        a: "Transcripts, yes, one at a time. Copy a transcript from Granola, save it as a text file, and import it in Chief. Your Granola summaries and folders stay in Granola.",
        href: SRC.granolaTranscription,
      },
      {
        q: "Is there a free Granola alternative?",
        a: `Yes. ${FREE_PLAN} Granola Basic is free too, with 30 days of meeting history.`,
        href: SRC.granolaPricing,
      },
    ],
  },
  {
    key: "otter",
    name: "Otter",
    checked: "Oct 3, 2026",
    title: "Otter Alternative With No Bot in Your Calls | Chief",
    description:
      "Looking for an Otter alternative? Chief records from your desktop with no bot, and you leave with decisions and to-dos sorted. See pricing and where Otter fits better.",
    h1: "An Otter alternative\nthat leaves you with the work started",
    answer:
      "If you want an Otter alternative with no bot in your calls, Chief records from your desktop on macOS and Windows. You leave the call with decisions and to-dos sorted, and Chief updates Linear, GitHub or Slack once you say yes. Otter still fits better if you record on Android.",
    whyLook: [
      {
        lead: "A bot joins your meetings.",
        text: "Otter's notetaker joins Zoom, Microsoft Teams and Google Meet to write and share notes. Its desktop app can record without the bot.",
        href: SRC.otterPricing,
      },
      {
        lead: "The free plan runs out.",
        text: "Otter Basic includes 300 transcription minutes a month, 30 minutes per conversation and 3 lifetime file imports, and keeps your 25 most recent conversations.",
        href: SRC.otterPricing,
      },
    ],
    quote: {
      text: "my pet peeve is that these ai notetakers, like otter arrive to the web conf before their user.",
      who: "fantasizr",
      where: "Hacker News",
      date: "2026-07-07",
      href: "https://news.ycombinator.com/item?id=48823442",
    },
    compare: [
      {
        area: "Capture",
        title: "Otter's bot is optional.\nChief never sends one.",
        text: "Both can record without a bot: Otter through its desktop app, Chief always. Both record in person on iPhone, and Otter also records on Android.",
        hrefs: [SRC.otterApps],
      },
      {
        area: "During the call",
        title: "Otter shares the transcript.\nChief shares the work.",
        text: "Otter Live Notes gives everyone a shared transcript to highlight, comment on and assign action items in. In Chief, people with editing access write shared Documents and Canvas while you talk, and Chief offers work you can accept.",
        hrefs: [SRC.otterBusiness],
      },
      {
        area: "After the call",
        title: "Otter writes to your tools on Enterprise.\nChief does it on its Pro plan.",
        text: "On Enterprise, Otter's AI Chat can read from and write to tools like Notion, Jira and Gmail through MCP. On its Pro plan, Chief updates Google, Notion, Slack, Linear and GitHub, and writes wait for your yes unless you pre-approved that kind of write.",
        hrefs: [SRC.otterMcp, SRC.otterPricing],
      },
      {
        area: "Your setup",
        title: "Your own model keys on any plan.\nAn MCP server that writes too.",
        text: "Otter offers an MCP server for AI assistants. Chief's MCP server reads and writes, you can bring your own Anthropic, OpenAI or OpenRouter keys on any plan, and your coding agent can start from the session.",
        hrefs: [SRC.otterPricing],
      },
    ],
    pricing: {
      tiers: [
        { name: "Basic", price: "$0", note: "300 minutes a month" },
        { name: "Pro", price: "$16.99 per user a month", note: "$8.33 billed annually" },
        { name: "Business", price: "$30 per user a month", note: "$19.99 billed annually" },
        { name: "Enterprise", price: "Custom" },
      ],
      team: "A team of five on Otter Business pays $150 a month billed monthly, or $99.95 a month billed annually.",
      href: SRC.otterPricing,
    },
    switchIf: [
      "You don't want a bot in the participant list on any call.",
      "You want to leave the call with decisions and to-dos sorted, and the plan already started.",
      "Your work runs through Linear, GitHub or Slack, and you want Chief to update them once you approve.",
      "You use a coding agent and want it to start from the conversation.",
    ],
    stayIf: [
      {
        lead: "You record on Android.",
        text: "Otter has iOS and Android apps. Chief's mobile app is iPhone only.",
        href: SRC.otterApps,
      },
      {
        lead: "You want to record in a browser.",
        text: "Otter records on the web. Chief records from a desktop app.",
        href: SRC.otterPricing,
      },
      {
        lead: "You import a lot of recorded audio.",
        text: "Otter Business takes unlimited files, up to 6,000 minutes per user a month.",
        href: SRC.otterPricing,
      },
    ],
    movingOver: {
      text: "Otter exports a transcript as a TXT file on every plan. Chief imports TXT, SRT, VTT, DOCX and CSV transcripts, and turns each one into a session with a summary, decisions and to-dos. Import the meetings you still refer to, then run your next call in Chief next to Otter.",
      href: SRC.otterPricing,
    },
    faq: [
      {
        q: "Is there a free Otter alternative?",
        a: `Yes. ${FREE_PLAN} Otter Basic is free too, with 300 transcription minutes a month.`,
        href: SRC.otterPricing,
      },
      {
        q: "Can I use Chief without a bot joining my calls?",
        a: "Yes, always. Chief records from the desktop app on macOS or Windows, so nothing joins the call. Otter can do the same through its desktop app.",
        href: SRC.otterApps,
      },
      {
        q: "Can I bring my Otter transcripts into Chief?",
        a: "Yes, one at a time. Export a transcript from Otter as TXT, then import it in Chief. Each import becomes a session with a summary, decisions and to-dos.",
        href: SRC.otterPricing,
      },
      {
        q: "Does Chief sync to Salesforce or HubSpot like Otter?",
        a: `Through MCP, not an automatic sync. ${HUB_LINE} Otter syncs notes to Salesforce and HubSpot from Pro, for 1 user on Pro and 5 on Business.`,
        href: SRC.otterPricing,
      },
    ],
  },
  {
    key: "fathom",
    name: "Fathom",
    checked: "Oct 3, 2026",
    title: "Fathom Alternative With No Bot on Mac or Windows | Chief",
    description:
      "Looking for a Fathom alternative? Chief records from your desktop on Mac and Windows with no bot, and updates Linear and GitHub once you approve. See where Fathom fits better.",
    h1: "A Fathom alternative\nthat turns the call into the next piece of work",
    answer:
      "If you want a Fathom alternative with no bot on Mac or Windows, Chief records from your desktop on both. You leave with decisions and to-dos sorted, and Chief updates Linear or GitHub once you approve. Fathom still fits better if you want unlimited free recording or automatic CRM sync.",
    whyLook: [
      {
        lead: "Bot-free is Mac only so far.",
        text: "Fathom joins with a bot, or records without one in its bot-free beta, which is Mac only today.",
        href: SRC.fathomBotFree,
      },
      {
        lead: "Engineering tools go through automation.",
        text: "Fathom connects natively to Slack, Asana, Notion, HubSpot and Salesforce. Linear, Jira and GitHub go through Zapier or Make.",
        href: SRC.fathomIntegrations,
      },
    ],
    quote: {
      text: "On Fathom, bot-free support for additional platforms and Windows is still rolling out.",
      who: "Zachary Proser",
      where: "personal blog",
      date: "2026-08-02",
      href: "https://zackproser.com/blog/best-ai-meeting-notes-2026",
    },
    compare: [
      {
        area: "Capture",
        title: "Bot-free on Mac and Windows,\nnot a Mac beta.",
        text: "Chief records without a bot on macOS and Windows, and in person on iPhone. Fathom's bot-free mode is a Mac beta. Fathom's iPhone app is on the App Store, while Fathom's site still offers a waitlist.",
        hrefs: [SRC.fathomBotFree, SRC.fathomIos],
      },
      {
        area: "During the call",
        title: "Fathom's scratchpad is private.\nChief's workspace is shared.",
        text: "Fathom gives you a live summary, a private scratchpad and teammate tags during the meeting. In Chief, decisions and to-dos are tagged as the call goes, and people with editing access write shared Documents and Canvas together.",
        hrefs: [SRC.fathomOverview],
      },
      {
        area: "After the call",
        title: "Fathom feeds your CRM.\nChief updates Linear and GitHub.",
        text: "Fathom pushes summaries to HubSpot and Salesforce and turns action items into Asana tasks. On Pro, Chief updates Google, Notion, Slack, Linear and GitHub, and writes wait for your yes unless you pre-approved that kind of write.",
        hrefs: [SRC.fathomPricing, SRC.fathomIntegrations],
      },
      {
        area: "Your setup",
        title: "Both have an MCP server.\nOnly Chief's can write.",
        text: "Fathom's API and MCP server come on every plan, and the MCP server reads your meetings. Chief's MCP server reads and writes, you can bring your own model keys on any plan, and your coding agent can start from the session.",
        hrefs: [SRC.fathomPricing, SRC.fathomMcp],
      },
    ],
    pricing: {
      tiers: [
        { name: "Free", price: "$0", note: "Unlimited recordings" },
        { name: "Premium", price: "$20 a month", note: "$16 billed annually" },
        { name: "Team", price: "$19 per user a month", note: "$15 billed annually, 2 users minimum" },
        { name: "Business", price: "$34 per user a month", note: "$25 billed annually, 2 users minimum" },
        { name: "Enterprise", price: "Talk to sales" },
      ],
      team: "A team of five on Fathom Team pays $95 a month billed monthly, or $75 billed annually. Business for five is $170 monthly or $125 annually.",
      href: SRC.fathomPricing,
    },
    switchIf: [
      "You record on Windows and don't want a bot in the call.",
      "You want your team writing the plan together during the call.",
      "Your work runs through Linear or GitHub, and you'd rather approve an update than build an automation.",
      "You want an MCP server your tools can write to, and your coding agent working from the conversation.",
    ],
    stayIf: [
      {
        lead: "Unlimited free recording is your priority.",
        text: "Fathom's free plan includes unlimited recordings and transcriptions.",
        href: SRC.fathomPricing,
      },
      {
        lead: "You want sales calls synced to your CRM automatically.",
        text: "Fathom syncs summaries to HubSpot and Salesforce from its free plan for up to 3 users per domain, with field sync on Business. Chief writes to HubSpot, Salesforce, Attio and Close through MCP on Pro, once you approve.",
        href: SRC.fathomPricing,
      },
      {
        lead: "You want sales coaching.",
        text: "Fathom Business includes coaching metrics and AI scorecards.",
        href: SRC.fathomPricing,
      },
    ],
    movingOver: {
      text: "Fathom doesn't offer a transcript download, but every recorded call has a Copy Transcript button. Copy it, save it as a text file, and import it in Chief to get a session with a summary, decisions and to-dos. Then run your next call in Chief next to Fathom.",
      href: SRC.fathomTranscript,
    },
    faq: [
      {
        q: "Fathom is part of Superhuman now. Does that change anything?",
        a: "Superhuman announced the acquisition on September 14, 2026. Fathom's own statement says: \"Nothing changes today for Fathom users. Same app, same login, same product experience.\" We can't speak for their plans.",
        href: SRC.fathomSuperhuman,
      },
      {
        q: "Does Chief record without a bot on Windows?",
        a: "Yes. Chief records from the desktop app on macOS and Windows, with no bot. Fathom's bot-free beta is Mac only today.",
        href: SRC.fathomBotFree,
      },
      {
        q: "Can I bring my Fathom transcripts into Chief?",
        a: "Yes, one at a time. Copy the transcript in Fathom, save it as a text file, and import it in Chief.",
        href: SRC.fathomTranscript,
      },
      {
        q: "Does Chief sync to my CRM like Fathom?",
        a: `Through MCP, not an automatic sync. ${HUB_LINE} Fathom syncs summaries to HubSpot and Salesforce from its free plan, with field sync on Business.`,
        href: SRC.fathomPricing,
      },
    ],
  },
];

export type VendorKey = Vendor["key"];
